"use client";

import { useMemo, useState } from "react";
import { saveInterfaceText } from "../actions";
import { DEFAULT_MESSAGES, type MessageKey, type MessageOverrides } from "../messages";
import { langProps } from "./locale";
import { SaveBar, useSave } from "./SaveBar";
import { CARD, INPUT, PageTitle } from "./ui";

// Every line of interface text (navigation, buttons, form labels, page headings, Google titles and descriptions) in
// English and Arabic, side by side. Lines are grouped by where they appear; the search finds a line in either language.

const SECTIONS: { prefix: string[]; title: string }[] = [
  { prefix: ["site", "nav", "header", "sound", "contact.button"], title: "Header, menu and site name" },
  { prefix: ["footer"], title: "Footer" },
  { prefix: ["story", "expression", "work", "services.all", "services.moreOn"], title: "Home page" },
  { prefix: ["cta"], title: "Closing call to action" },
  { prefix: ["contact", "drawer", "form"], title: "Contact page and contact form" },
  { prefix: ["about", "projects"], title: "About and Projects pages" },
  { prefix: ["services", "process"], title: "Services page" },
  { prefix: ["service."], title: "Each service page" },
  { prefix: ["blog", "post", "category"], title: "Blog" },
  { prefix: ["faq"], title: "FAQ page" },
  { prefix: ["notFound"], title: "Page not found (404)" },
  { prefix: ["soon"], title: "Coming-soon page and game" },
  { prefix: ["meta"], title: "Google titles and descriptions" },
];

// The website's lines, known once its built-in text has loaded (messages.ts).
const keys = () => Object.keys(DEFAULT_MESSAGES.en) as MessageKey[];
// The most specific prefix wins ("services.all" is on the home page, other "services." lines on the Services page).
const sectionOf = (key: MessageKey) => {
  let best = -1;
  let length = -1;
  SECTIONS.forEach((s, i) =>
    s.prefix.forEach((p) => {
      if ((key === p || key.startsWith(p.endsWith(".") ? p : `${p}.`)) && p.length > length) {
        best = i;
        length = p.length;
      }
    }),
  );
  return best;
};

export function InterfaceTextEditor({ overrides }: { overrides: MessageOverrides }) {
  // What each field shows: the saved change, or the built-in text.
  const [values, setValues] = useState(() => ({
    en: Object.fromEntries(keys().map((k) => [k, overrides.en[k] ?? DEFAULT_MESSAGES.en[k]])) as Record<MessageKey, string>,
    ar: Object.fromEntries(keys().map((k) => [k, overrides.ar[k] ?? DEFAULT_MESSAGES.ar[k]])) as Record<MessageKey, string>,
  }));
  const [query, setQuery] = useState("");
  const { pending, notice, dirty, markDirty, run } = useSave();

  const set = (lang: "en" | "ar", key: MessageKey, value: string) => {
    markDirty();
    setValues((v) => ({ ...v, [lang]: { ...v[lang], [key]: value } }));
  };

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const shown = keys().filter((k) => !q || values.en[k].toLowerCase().includes(q) || values.ar[k].includes(query.trim()) || k.toLowerCase().includes(q));
    return SECTIONS.map((s, i) => ({ title: s.title, keys: shown.filter((k) => sectionOf(k) === i) })).filter((g) => g.keys.length);
  }, [query, values]);

  const changed = (lang: "en" | "ar", key: MessageKey) => values[lang][key] !== DEFAULT_MESSAGES[lang][key];

  return (
    <div>
      <PageTitle title="Interface text" />
      <p className="mb-6 text-midnight/70">
        Every button, menu item, label, page heading and Google title on the website, in English and Arabic. Change a line and save; empty a line
        to bring back the built-in text. Keep words in {"{braces}"} (like {"{name}"}): the website fills them in.
      </p>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search in English or Arabic…"
        aria-label="Search the interface text"
        className={`${INPUT} mb-6`}
      />
      <div className="flex flex-col gap-6">
        {groups.map((group) => (
          <section key={group.title} className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">{group.title}</h2>
            {group.keys.map((key) => {
              const long = DEFAULT_MESSAGES.en[key].length > 70 || DEFAULT_MESSAGES.ar[key].length > 70;
              return (
                <div key={key} className="flex flex-col gap-2 border-t border-midnight/10 pt-4 first-of-type:border-0 first-of-type:pt-0">
                  <p className="font-mono text-xs text-midnight/70">{key}</p>
                  <div className="grid gap-2 md:grid-cols-2">
                    {(["en", "ar"] as const).map((lang) => {
                      const props = {
                        "aria-label": `${key} in ${lang === "en" ? "English" : "Arabic"}`,
                        value: values[lang][key],
                        placeholder: DEFAULT_MESSAGES[lang][key],
                        onChange: (e: { target: { value: string } }) => set(lang, key, e.target.value),
                        className: `${INPUT} ${changed(lang, key) ? "border-teal bg-teal/5" : ""}`,
                        ...langProps(lang),
                      };
                      return long ? <textarea key={lang} rows={3} {...props} className={`${props.className} resize-y leading-relaxed`} /> : <input key={lang} {...props} />;
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        ))}
        {!groups.length && <p className="py-10 text-center text-midnight/70">Nothing matches “{query}”.</p>}
      </div>
      <SaveBar onSave={() => run(() => saveInterfaceText(values))} pending={pending} dirty={dirty} notice={notice} />
    </div>
  );
}
